import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { webddn } from 'src/environments/webddn';
import { ddnSessionToken } from '../utils/ddn-session';
import { DistributedLogService } from './distributed-log.service';

type SocketClient = import('socket.io-client').Socket;

interface WelcomeMessage {
  message: string;
  socketId: string;
  room: string;
}

export interface NotificationMessage {
  type: 'cancellation' | 'warning' | 'info';
  title: string;
  message: string;
  icon?: string;
  timeout?: number;
  tag?: string;
  requireInteraction: boolean;
  silent: boolean;
}

type PendingHandler = { event: string; handler: (...args: any[]) => void };
type PendingEmit = { event: string; data?: unknown };

@Injectable({
  providedIn: 'root'
})
export class SocketIoService {
  private socket: SocketClient | null = null;
  private notificationSubject = new Subject<NotificationMessage>();
  private connectionStatus = new BehaviorSubject<boolean>(false);
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private pendingCancelIds: string[] = [];
  /** In-flight dynamic import + first `io()` construction. */
  private connecting: Promise<void> | null = null;
  private pendingHandlers: PendingHandler[] = [];
  private pendingEmits: PendingEmit[] = [];

  constructor(
     private cookieService: CookieService,
     private distributedLog: DistributedLogService
   ) {
    this.ensureConnected();
   }

  private readAuth(): { token: string; company: string; traceId: string } | null {
    const token = ddnSessionToken(this.cookieService.get('ddnsession'));
    if (!token) return null;
    return {
      token,
      company: String(environment.COMPANY || webddn.DOMAIN || ''),
      // Linkage: let the server correlate its socket logs with this client's trace.
      traceId: this.distributedLog.traceIdValue,
     };
   }

  private socketUrl(): string {
    const apiDomain = environment.API_DOMAIN;
    return /^https?:\/\//i.test(apiDomain)
      ? apiDomain.replace(/\/api\/?$/, '')
      : `${environment.BASEURL}${apiDomain}`.replace(/\/api\/?$/, '');
  }

  /** Connect (or reconnect) with current session token. No-op if logged out. */
  ensureConnected(): void {
    const auth = this.readAuth();
    if (!auth) {
      this.disconnect();
      return;
    }
    
    // Defer socket connection on mobile to improve initial page load performance
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && !this.socket) {
      setTimeout(() => {
        if (!this.socket?.connected) {
          void this.initializeSocket(auth);
        }
      }, 5000); // Defer for 5 seconds on mobile
      return;
    }

    if (this.socket?.connected) {
      return;
    }
    if (this.socket) {
      this.socket.auth = auth;
      this.socket.connect();
      return;
    }
    void this.initializeSocket(auth);
  }

  private async initializeSocket(auth: { token: string; company: string; traceId: string }): Promise<void> {
    const existing = this.socket;
    if (existing) {
      existing.auth = auth;
      if (!existing.connected) existing.connect();
      return;
    }
    if (this.connecting) {
      await this.connecting;
      const sock = this.socket;
      const latest = this.readAuth();
      if (latest && sock && !sock.connected) {
        sock.auth = latest;
        sock.connect();
      }
      return;
    }

    this.connecting = (async () => {
      try {
        const { io } = await import('socket.io-client');
        const latest = this.readAuth();
        if (!latest) {
          return;
        }
        const already = this.socket;
        if (already) {
          already.auth = latest;
          if (!already.connected) already.connect();
          return;
        }
        const socketUrl = this.socketUrl();
        this.socket = io(socketUrl, {
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 20000,
          autoConnect: true,
          forceNew: false,
          withCredentials: true,
          auth: latest,
        });
        this.setupSocketListeners();
        this.flushPendingHandlers();
        this.flushPendingEmits();
      } catch (error) {
        console.error('Error initializing socket:', error);
        this.connectionStatus.next(false);
      }
    })();

    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  private flushPendingHandlers(): void {
    if (!this.socket || !this.pendingHandlers.length) return;
    const queued = this.pendingHandlers;
    this.pendingHandlers = [];
    for (const { event, handler } of queued) {
      this.socket.on(event, handler);
    }
  }

  private flushPendingEmits(): void {
    if (!this.socket || !this.pendingEmits.length) return;
    const queued = this.pendingEmits;
    this.pendingEmits = [];
    for (const { event, data } of queued) {
      this.socket.emit(event, data);
    }
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus.asObservable();
  }

  emit(event: string, data?: unknown): void {
    if (!this.socket?.connected) {
      this.ensureConnected();
    }
    if (this.socket) {
      this.socket.emit(event, data);
      return;
    }
    if (this.readAuth()) {
      this.pendingEmits.push({ event, data });
    }
  }

  /** Subscribe; returns unsubscribe fn. */
  on(event: string, handler: (...args: any[]) => void): () => void {
    this.ensureConnected();
    if (this.socket) {
      this.socket.on(event, handler);
    } else {
      this.pendingHandlers.push({ event, handler });
    }
    return () => {
      this.socket?.off(event, handler);
      this.pendingHandlers = this.pendingHandlers.filter(
        (p) => !(p.event === event && p.handler === handler)
      );
    };
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }
    return await Notification.requestPermission();
  }

  sendCancelNotification(notificationId: string): void {
    if (!this.socket?.connected || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      if (!this.pendingCancelIds.includes(notificationId)) {
        this.pendingCancelIds.push(notificationId);
      }
      return;
    }
    this.socket.emit('cancel', { id: notificationId });
  }

  emitCancelEvent(): void {
    if (!this.socket?.connected || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return;
    }
    this.socket.emit('cancel', {
      timestamp: new Date().toISOString()
    });
  }

  getCancelNotification(): Observable<NotificationMessage> {
    return this.notificationSubject.asObservable();
  }

  reconnect(): void {
    this.reconnectAttempts = 0;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.ensureConnected();
  }

  private flushPendingCancels(): void {
    if (!this.socket?.connected || !this.pendingCancelIds.length) return;
    const ids = [...this.pendingCancelIds];
    this.pendingCancelIds = [];
    for (const id of ids) {
      this.socket.emit('cancel', { id });
    }
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connectionStatus.next(true);
      this.reconnectAttempts = 0;
      this.flushPendingEmits();
      this.flushPendingCancels();
    });

    this.socket.on('welcome', (data: WelcomeMessage) => {
      console.log('Received welcome from server:', data);
    });

    this.socket.on('cancel-notification', (data: NotificationMessage) => {
      this.notificationSubject.next(data);
    });

    this.socket.on('disconnect', (reason: string) => {
      this.connectionStatus.next(false);
      if (reason === 'io server disconnect' || reason === 'transport close') {
        this.manualReconnect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      this.connectionStatus.next(false);
      try {
        const transports = (this.socket?.io.opts.transports || []) as string[];
        if (transports.indexOf('websocket') !== -1 && this.socket) {
          this.socket.io.opts.transports = ['polling'];
        }
      } catch (e) {
        console.warn('Error checking transport options:', e);
      }
    });
  }

  private manualReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    this.reconnectAttempts++;
    const delay = Math.min(30000, 1000 * Math.pow(1.5, this.reconnectAttempts - 1));
    setTimeout(() => {
      const auth = this.readAuth();
      if (!auth) return;
      if (this.socket) {
        this.socket.auth = auth;
        this.socket.connect();
      } else {
        void this.initializeSocket(auth);
      }
    }, delay);
  }

  async onNotificationCancelled(notificationMessage: NotificationMessage): Promise<Observable<NotificationMessage>> {
    try {
      if (!('Notification' in window)) {
        return this.notificationSubject.asObservable();
      }
      // Only show a native notification if permission is already granted.
      // Do NOT call requestPermission() here — socket events are not user
      // gestures; calling it here triggers the Lighthouse "notification on
      // page load" warning. Permission is requested lazily from user-gesture
      // handlers (e.g. bell-icon click → showNotification).
      if (Notification.permission === 'granted') {
        const notification = new Notification(notificationMessage.title, {
          body: notificationMessage.message,
          icon: notificationMessage.icon || 'assets/cancel-icon.png',
          tag: notificationMessage.tag || 'cancellation',
          requireInteraction: notificationMessage.requireInteraction || false,
          silent: notificationMessage.silent || false
        });
        notification.onclick = () => {
          notification.close();
          window.focus();
        };
        setTimeout(() => notification.close(), 5000);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
    return this.notificationSubject.asObservable();
  }

  disconnect(): void {
    this.pendingEmits = [];
    this.pendingHandlers = [];
    this.connecting = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionStatus.next(false);
  }
}
